import { useNavigate, useLocation } from "react-router";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import endpoints from "../../services/endpoints";
import { TableDetailsItem } from "../../components/tables/tableFunctions";
import { useEffect, useState, useCallback, useMemo } from "react";
import usePrivilege from "../../components/CustomHooks/usePrivilege";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { setPreviousUrl, setUserUrl } from "../../store/slices/geolocationSlice";
import { resetPhoneNumbers } from "../../store/slices/phoneNumbersSlice";
import GridTable from "../../components/GridTable/GridTable";
import { DynamicFilterConfig } from "../../components/DynamicComponent/DynamicFilterPanel";

export default function ReversalRequest() {
  const location = useLocation();
  const navigate = useNavigate();
  let currentUrl = location.pathname + location.search;
  const dispatch = useDispatch<AppDispatch>();
  const { reversalRequest } = endpoints;
  const [searchPlaceholder, setSearchPlaceholder] =
    useState<string>("transaction id");
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const viewPrivilege = usePrivilege('RR2');

  const TableDetails: { [key: string]: TableDetailsItem } = {
    "Transaction ID": {
      id: "transaction_id",
      className: "min-w-[140px]",
    },
    "Transaction Date and Time": {
      id: "transaction_date",
      custom: ["transaction_date"],
      className: "min-w-[240px]",
    },
    "Requested By": {
      id: "requested_by",
      className: "min-w-[220px]",
    },
    "Requested From": {
      id: "requested_from",
      className: "min-w-[220px]",
    },
    Amount: {
      id: "amount",
      custom: ["amount"],
    },
    "Transaction Type": {
      id: "transaction_category",
      custom: ["transaction_category"],
    },
    "Reversal Status": {
      id: "flag_status",
    },
    Actions: {
      id: "actions",
      className: "min-w-[200px]",
      actionTypes: ["view"],
    },
  };
  const SEARCH_PLACEHOLDERS = [
      'requested by',
      'requested from',
      'transaction id'
    ] as const;
  
    const rotatePlaceholder = useCallback(() => {
      setIsTransitioning(true);
  
      setTimeout(() => {
        setSearchPlaceholder(prev => {
          const currentIndex = SEARCH_PLACEHOLDERS.indexOf(prev as typeof SEARCH_PLACEHOLDERS[number]);
          const nextIndex = (currentIndex + 1) % SEARCH_PLACEHOLDERS.length; // Loop cyclically
          return SEARCH_PLACEHOLDERS[nextIndex];
        });
        setIsTransitioning(false);
      }, 500);
    }, []);
  
    useEffect(() => {
      const interval = setInterval(rotatePlaceholder, 2500);
      return () => clearInterval(interval);
    }, [rotatePlaceholder]);

  useEffect(() => {
    dispatch(setUserUrl(null));
    dispatch(setPreviousUrl(currentUrl));
    dispatch(resetPhoneNumbers());
  }, [currentUrl]);

  const handleActions = async (id: string, key: string, text?: string) => {
    navigate(`/reversal-requests/view-reversal-request/${id}`);
  };

  const staticText = {
    emptyScreenText: {
      heading1: `There are no transactions yet`,
      heading2: ``,
    },
  };

  // Dynamic filter config for transactions
  const dynamicFilterConfig: DynamicFilterConfig = {
    "Date Range": {
      label: "Date Range",
      type: "date",
      required: false,
      searchKey: {
        start: "transaction_start_date",
        end: "transaction_end_date",
      },
    },
    "Reversal Status": {
      label: "Reversal Status",
      type: "checkbox",
      options: ["Approved", "Pending", "Rejected"],
      required: false,
      searchKey: "reversal_status",
    },
    "Transaction Type": {
      label: "Transaction Type",
      type: "radio",
      options: ["All", "eTop Up", "Recharge"],
      default: "All",
      required: false,
      searchKey: "transaction_category",
    },
  };

  return (
    <div>
      <Breadcrumbs
        navPath={["Reversal Request", `List of Reversal Request Transactions`]}
        heading={`List of Reversal Request Transactions`}
        links={["/reversal-requests"]}
        list={true}
      />
      <GridTable
        staticText={staticText}
        mainEndPoint={reversalRequest}
        TableDetails={TableDetails}
        handleActions={handleActions}
        params={{ page_number: "1" }}
        searchPlaceholder={`Search by ${searchPlaceholder}`}
        filters={true}
        exporting={true}
        notfoundRoute={{ state: { route: `/reversal-requests` } }}
        title={`View Transaction Details`}
        dynamicFilterConfig={dynamicFilterConfig}
        hasActivateDeactivateMDPrivilege={true}
        hasViewMDPrivilege={viewPrivilege}
        hasEditMDPrivilege={true}
        addMorePadding={false}
        forceFullWidth={true}
      />
    </div>
  );
}